import * as React from 'react';
import { LoaderCircleIcon } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { useTenantEntity } from '@/components/access/tenant-entity-provider';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';

const loadNavigationAdmin = async () => ({
  default: (await import('@/components/admin/navigation-layout-admin')).NavigationLayoutAdmin
});
const loadCustomPageBuilder = async () => ({
  default: (await import('@/pages/admin/custom-page-builder')).AdminCustomPageBuilderPage
});

class ManageDrawerErrorBoundary extends React.Component<{
  children: React.ReactNode;
  onRetry: () => void;
}, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return (
        <div role="alert" className="space-y-3 rounded-lg border p-4 text-sm">
          <p>The management screen could not open. Try again, or close this drawer to return to your page.</p>
          <Button type="button" variant="outline" onClick={this.props.onRetry}>Try again</Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export type ManageTarget =
  | { kind: 'navigation' }
  | { kind: 'custom-page'; id: string; title: string };

type ManageModeState = {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  toggle: () => void;
  target: ManageTarget | null;
  open: (target: ManageTarget) => void;
  close: () => void;
};

const ManageModeContext = React.createContext<ManageModeState>({
  enabled: true,
  setEnabled: () => undefined,
  toggle: () => undefined,
  target: null,
  open: () => undefined,
  close: () => undefined
});

function DrawerLoading() {
  return (
    <div className="text-muted-foreground flex min-h-48 items-center justify-center gap-2 text-sm" role="status">
      <LoaderCircleIcon className="size-4 animate-spin" aria-hidden />
      Loading management screen…
    </div>
  );
}

function ManageDrawerBody({ target }: { target: ManageTarget }) {
  const access = useTenantEntity();
  const [attempt, setAttempt] = React.useState(0);
  // A rejected lazy import remains cached by React. A retry needs fresh owners.
  const { NavigationLayoutAdmin, AdminCustomPageBuilderPage } = React.useMemo(() => ({
    NavigationLayoutAdmin: React.lazy(loadNavigationAdmin),
    AdminCustomPageBuilderPage: React.lazy(loadCustomPageBuilder)
  }), [attempt]);
  const canManageNavigation = access.hasPermission('entity.manage');
  const canManagePages = access.hasPermission('tenant.members.manage');
  const allowed = target.kind === 'navigation'
    ? canManageNavigation
    : target.kind === 'custom-page'
      ? canManagePages
      : false;
  if (!allowed) {
    return (
      <div className="rounded-lg border p-4 text-sm" role="alert">
        You do not currently have permission to manage this area.
      </div>
    );
  }

  return (
    <ManageDrawerErrorBoundary key={attempt} onRetry={() => setAttempt((value) => value + 1)}>
      <React.Suspense fallback={<DrawerLoading />}>
        {target.kind === 'navigation' && <NavigationLayoutAdmin />}
        {target.kind === 'custom-page' && <AdminCustomPageBuilderPage pageId={target.id} />}
      </React.Suspense>
    </ManageDrawerErrorBoundary>
  );
}

function ManageDrawer({ target, close }: { target: ManageTarget | null; close: () => void }) {
  const { selected } = useTenantEntity();
  const bodyKey = `${selected?.tenantId}:${selected?.entityId}:${target?.kind}:${target?.kind === 'custom-page' ? target.id : ''}`;
  const title = target?.kind === 'navigation' ? 'Manage navigation' : target?.title ?? 'Manage page';
  const description = target?.kind === 'navigation'
    ? 'Arrange the left-hand menu using the existing navigation management screen.'
    : 'Edit the widgets and content shown on this custom page.';

  return (
    <Sheet open={Boolean(target)} onOpenChange={(nextOpen) => !nextOpen && close()}>
      <SheetContent
        side="right"
        overlayClassName="bg-black/40"
        className="flex w-[min(96vw,78rem)] max-w-none flex-col overflow-hidden p-0 sm:max-w-[min(92vw,78rem)]"
      >
        <SheetHeader className="border-b px-6 py-5 pr-12 text-left">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {target && <ManageDrawerBody key={bodyKey} target={target} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function ManageModeProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [enabled, setEnabledState] = React.useState(false);
  const [target, setTarget] = React.useState<ManageTarget | null>(null);
  const [announcement, setAnnouncement] = React.useState('');
  const previousLocation = React.useRef(`${location.pathname}${location.search}`);

  React.useEffect(() => {
    const currentLocation = `${location.pathname}${location.search}`;
    if (currentLocation !== previousLocation.current) {
      previousLocation.current = currentLocation;
      setTarget(null);
    }
  }, [location.pathname, location.search]);

  const setEnabled = React.useCallback((nextEnabled: boolean) => {
    setEnabledState(nextEnabled);
    if (!nextEnabled) setTarget(null);
    setAnnouncement(`Manage mode ${nextEnabled ? 'enabled' : 'disabled'}`);
  }, []);
  const toggle = React.useCallback(() => setEnabled(!enabled), [enabled, setEnabled]);
  const open = React.useCallback((nextTarget: ManageTarget) => setTarget(nextTarget), []);
  const close = React.useCallback(() => setTarget(null), []);
  const value = React.useMemo(
    () => ({ enabled, setEnabled, toggle, target, open, close }),
    [close, enabled, open, setEnabled, target, toggle]
  );

  return (
    <ManageModeContext.Provider value={value}>
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </span>
      {children}
      <ManageDrawer target={target} close={close} />
    </ManageModeContext.Provider>
  );
}

export function useManageMode() {
  return React.useContext(ManageModeContext);
}
